import { Component, Prop, Ref } from '@eastsideco/salvo-ts';
import { ThemeComponent } from '@/theme/ThemeComponent';
import axios from 'axios';

interface GeoserviceInfoResponse {
    result: GeoserviceInfo | null;
}

interface GeoserviceInfo {
    country: {
        // eslint-disable-next-line @typescript-eslint/naming-convention
        iso_code: string;
    };
    continent: {
        code: string;
    };
}

@Component
export class GeoIp extends ThemeComponent {
    @Prop currentCountry!: string;

    @Ref() geoIPSelectors!: HTMLDivElement;

    @Ref() geoIpBackdrop!: HTMLDivElement;

    @Ref() storesListContainer!: HTMLDivElement;
    @Ref() languagesListContainer!: HTMLDivElement;

    @Ref() storesListToggle!: HTMLButtonElement;
    @Ref() languagesListToggle!: HTMLButtonElement;

    @Ref() storesList!: HTMLUListElement;
    @Ref() languagesList!: HTMLUListElement;


    @Ref() storeOptions!: HTMLButtonElement[];
    @Ref() languageOptions!: HTMLButtonElement[];

    @Ref() continueButton!: HTMLLinkElement;

    private availableAlternativeCountries: string[] = [
        'GB',
        'US',
        'AU',
        'DE',
        'FR',
        'NL',
        'IT',
        'ES'
    ];

    async init() {
        if (this.checkPopupIsHidden()) {
            return;
        }

        const locationData = await this.getLocationData();

        if (locationData === null) {
            this.$log.error(`Error whilst getting location data`);
            return;
        }

        if (!this.shouldShowPopup(locationData)) {
            return;
        }

        this.openPopup(); // Generic popup triggered on links.

        this.createDropdownToggleListeners();
        this.createStoreSelectListeners();
        this.createLanguageSelectListeners();
        this.handleContinueButtonClick();
    }

    checkPopupIsHidden(): boolean {
        const preventDisplay = sessionStorage.getItem('preventGeoIpDisplay');
        const urlParams = new URLSearchParams(window.location.search);
        const forceDisplaySearchString = urlParams.get("forceGeoIpDisplay");
        const preventDisplaySearchString = urlParams.get("preventGeoIpDisplay");

        if (forceDisplaySearchString === "1" || forceDisplaySearchString === "true") {
            return false;
        }

        return preventDisplay === "1"
            || preventDisplay === "true"
            || preventDisplaySearchString === "1"
            || preventDisplaySearchString === "true";
    }

    openPopup() {
        this.classList.remove("geo-ip--hidden");

        // Close main mobile menu.
        document.dispatchEvent(new Event('closeMenu'));
        document.dispatchEvent(new Event('hamburgerClose'));
        if (document.body.classList.contains('search-open')) {
            document.dispatchEvent(new Event('searchClose'));
        }
    }

    setBackdropVisibility(isVisible: boolean) {
        this.geoIpBackdrop.classList.toggle("geo-ip__content__backdrop--visible", isVisible);
    }

    closeAllDropdowns() {
        this.setBackdropVisibility(false);

        this.storesListToggle.classList.remove("geo-ip__selectors__dropdown-toggle--open");
        this.storesList.classList.add("geo-ip__selectors__dropdown--hidden");

        this.languagesListToggle.classList.remove("geo-ip__selectors__dropdown-toggle--open");
        this.languagesList.classList.add("geo-ip__selectors__dropdown--hidden");
    }

    createDropdownToggleListeners() {
        this.$listen(this.storesListToggle, "click", () => {
            this.setBackdropVisibility(true);

            this.storesListToggle.classList.add("geo-ip__selectors__dropdown-toggle--open");
            this.storesList.classList.remove("geo-ip__selectors__dropdown--hidden");
        });

        this.$listen(this.languagesListToggle, "click", () => {
            this.setBackdropVisibility(true);

            this.languagesListToggle.classList.add("geo-ip__selectors__dropdown-toggle--open");
            this.languagesList.classList.remove("geo-ip__selectors__dropdown--hidden");
        });

        this.$listen(this.geoIpBackdrop, "click", () => {
            this.closeAllDropdowns();
        });

        this.$listen(document.body, "keydown", (event) => {
            if (event.code === "Escape") {
                this.closeAllDropdowns();
            }
        });
    }

    setSelectedButton(buttons: HTMLButtonElement[], selectedButton: HTMLButtonElement) {
        for (const button of buttons) {
            button.dataset.selected = "false";
        }

        selectedButton.dataset.selected = "true";
    }

    createStoreSelectListeners() {
        for (const button of this.storeOptions) {
            this.$listen(button, "click", () => {
                const storeIsMultiLanguage = button.dataset.multiLanguage === "true";

                this.setSelectedButton(this.storeOptions, button);
                this.setBackdropVisibility(false);
                this.storesListToggle.classList.remove("geo-ip__selectors__dropdown-toggle--open");
                this.storesList.classList.add("geo-ip__selectors__dropdown--hidden");

                this.geoIPSelectors.classList.toggle("geo-ip__selectors--language-hidden", !storeIsMultiLanguage);
                this.languagesListContainer.classList.toggle(
                    "geo-ip__selectors__dropdown-container--hidden",
                    !storeIsMultiLanguage
                );

                const textEl = this.storesListToggle.querySelector("span");

                if (textEl) {
                    textEl.innerText = button.innerText;
                }

                this.updateStoreURL();
            });
        }
    }

    createLanguageSelectListeners() {
        for (const button of this.languageOptions) {
            this.$listen(button, "click", () => {
                this.setSelectedButton(this.languageOptions, button);
                this.setBackdropVisibility(false);
                this.languagesListToggle.classList.remove("geo-ip__selectors__dropdown-toggle--open");
                this.languagesList.classList.add("geo-ip__selectors__dropdown--hidden");

                const textEl = this.languagesListToggle.querySelector("span");

                if (textEl) {
                    textEl.innerText = button.innerText;
                }

                this.updateStoreURL();
            });
        }
    }

    updateStoreURL() {
        let url: string | undefined;

        for (const storeButton of this.storeOptions) {
            if (storeButton.dataset.selected === "true" && storeButton.dataset.store) {
                url = storeButton.dataset.store;

                if (storeButton.dataset.multiLanguage === "true") {
                    for (const languageButton of this.languageOptions) {
                        if (languageButton.dataset.selected === "true" && languageButton.dataset.language) {
                            url = `${url}?langView=${languageButton.dataset.language.toLowerCase()}`;
                            break;
                        }
                    }
                }

                break;
            }
        }

        if (url) {
            this.continueButton.href = url;
            this.continueButton.disabled = false;
        } else {
            this.continueButton.href = "";
            this.continueButton.disabled = true;
        }
    }

    handleContinueButtonClick() {
        this.$listen(this.continueButton, "click", () => {
            sessionStorage.setItem("preventGeoIpDisplay", "true");
        });
    }


    async getLocationData(): Promise<GeoserviceInfo|null> {
        // You can set _geoservice_ip in your local storage to pretend you're visting from another country.
        // instructions: https://developer.chrome.com/docs/devtools/storage/localstorage/
        // EXAMPLES:
        // New York, US: 72.229.28.185
        // Madrid, Spain: 95.100.115.77
        // Lisbon, Portugal: 104.89.164.91
        // Cremona, Italy: 81.88.228.195
        // Aus, 110.33.122.75
        const geoIpOverride = window.localStorage.getItem('_geoservice_ip');

        const response = await axios.get<GeoserviceInfoResponse>('https://geo.eastsideapps.io/api/v1/info', {
            params: {
                ip: geoIpOverride || undefined,
            }
        });

        if (response.status === 200) {
            return response.data.result;
        }

        return null;
    }

    shouldShowPopup(locationData: GeoserviceInfo) {
        // Get specific data
        const country = locationData.country.iso_code;
        const urlParams = new URLSearchParams(window.location.search);
        const forceDisplaySearchString = urlParams.get("forceGeoIpDisplay");

        const isEU = this.currentCountry === "EU" && ['DE', 'FR', 'NL', 'IT', 'ES'].includes(country);

        if (forceDisplaySearchString !== "1" && forceDisplaySearchString !== "true") {
            // Check if country is current or is an available country.
            if (country === this.currentCountry || isEU || !this.availableAlternativeCountries.includes(country)) {
                sessionStorage.setItem('preventGeoIpDisplay', 'true');
                return false;
            }
        }

        return true;
    }

    closePopup(setStorage = true) {
        if(setStorage) {
            sessionStorage.setItem("preventGeoIpDisplay", "true");
        }

        this.classList.add("geo-ip--hidden");
    }
}
